import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Box, Typography, Chip, Tooltip, IconButton, Modal, Fade, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '92vw',
  maxWidth: 1100,
  height: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const COL = {
  idDate: 155,
  type: 105,
  tax: 80,
  amount: 120,
  applicant: 130,
  actions: 50,
};

const HEADERS = [
  { label: 'ID / Request Date', key: 'idDate', width: COL.idDate },
  { label: 'Order', key: 'order', flex: 1 },
  { label: 'Type', key: 'type', width: COL.type, align: 'center' },
  { label: 'ภาษี', key: 'tax', width: COL.tax, align: 'center' },
  { label: 'ยอดจ่ายจริง', key: 'amount', width: COL.amount, align: 'right' },
  { label: 'Payee', key: 'payee', flex: 1.5 },
  { label: 'Applicant', key: 'applicant', width: COL.applicant },
  { label: '', key: 'actions', width: COL.actions, align: 'center' },
];

export default function ExpensesTable({ expenses, payment }) {
  const expenseRowsAll = useSelector((state) => state.expenses?.rows || []);
  const [detailId, setDetailId] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const expenseMap = React.useMemo(
    () => _.keyBy(expenseRowsAll, (e) => String(e._id)),
    [expenseRowsAll],
  );

  const fmt = (n) =>
    (typeof n === 'number' ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2 });

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.8, px: 0.5 }}>
        รายละเอียดรายการเบิก ({expenses.length} รายการ)
      </Typography>

      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflow: 'hidden', overflowX: 'auto' }}>
        {/* Column headers */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 0.7,
            backgroundColor: '#f1f5f9',
            borderBottom: '1px solid #e2e8f0',
            minWidth: 1010,
          }}
        >
          {HEADERS.map((col) => (
            <Typography
              key={col.key}
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              noWrap
              sx={{ ...(col.flex ? { flex: col.flex, minWidth: 0 } : { width: col.width, flexShrink: 0 }), textAlign: col.align || 'left' }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {expenses.map((item, idx) => {
          const fullExp = expenseMap[String(item.expenses_id)];
          const code = fullExp?.code || fullExp?.expenses_code || item.expenses_code || String(item.expenses_id || '').slice(-8);
          const dateRequest = fullExp?.date
            ? dayjs(fullExp.date).format('DD/MM/YYYY')
            : (fullExp?.dateRequest ? dayjs(fullExp.dateRequest).format('DD/MM/YYYY') : '-');
          const expensesType = fullExp?.expenses_type;
          const expenseName = fullExp?.name || item.name || '-';
          const includesVat = fullExp?.includes_vat;
          const withholdingTax = fullExp?.withholding_tax || 0;
          const withholdingRate = fullExp?.withholding_tax_rate;
          const amount = fullExp
            ? (fullExp.net_price ?? ((fullExp.price || 0) - (fullExp.withholding_tax || 0)))
            : (item.total || item.price || 0);
          const payee = fullExp?.payee || payment?.payee;
          const applicant = fullExp?.employee
            ? formatEmployeeThaiName(fullExp.employee)
            : formatEmployeeThaiName(payment?.requester) || '-';

          return (
            <Box
              key={item.expenses_id || idx}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 2,
                py: 0.9,
                borderBottom: '1px solid #f1f5f9',
                minWidth: 1010,
                '&:hover': { backgroundColor: '#f8fafc' },
              }}
            >
              {/* ID / Request Date */}
              <Box sx={{ width: COL.idDate, flexShrink: 0 }}>
                <Typography variant="body2" fontWeight={700} fontSize="0.78rem" color="#1d4ed8">{code}</Typography>
                <Typography variant="caption" color="text.secondary">{dateRequest}</Typography>
              </Box>

              {/* Order */}
              <Typography variant="body2" fontSize="0.78rem" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {expenseName}
              </Typography>

              {/* Type */}
              <Box sx={{ width: COL.type, flexShrink: 0, display: 'flex', justifyContent: 'center', pt: 0.2 }}>
                {expensesType === 'PAY' ? (
                  <Chip size="small" label="เบิกเพื่อจ่าย" color="primary" sx={{ fontSize: '0.6rem', height: 18 }} />
                ) : expensesType === 'REFUND' ? (
                  <Chip size="small" label="เบิกคืน" color="secondary" sx={{ fontSize: '0.6rem', height: 18 }} />
                ) : (
                  <Chip size="small" label="-" sx={{ fontSize: '0.6rem', height: 18 }} />
                )}
              </Box>

              {/* ภาษี */}
              <Box sx={{ width: COL.tax, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                {includesVat && <Chip size="small" label="VAT 7%" color="success" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />}
                {withholdingTax > 0 && <Chip size="small" label={`หัก${withholdingRate ? ` ${withholdingRate}%` : ''}`} color="warning" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />}
                {!includesVat && !withholdingTax && <Typography variant="caption" color="text.disabled">-</Typography>}
              </Box>

              {/* ยอดจ่ายจริง */}
              <Box sx={{ width: COL.amount, flexShrink: 0, textAlign: 'right' }}>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#1e293b', pt: 0.3 }}>
                  ฿{fmt(amount)}
                </Typography>
              </Box>

              {/* Payee */}
              <Box sx={{ flex: 1.5, minWidth: 0 }}>
                <Typography variant="body2" fontSize="0.75rem" noWrap>{payee?.name || '-'}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{payee?.bank || ''}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{payee?.account_number || ''}</Typography>
              </Box>

              {/* Applicant */}
              <Typography variant="body2" fontSize="0.78rem" noWrap sx={{ width: COL.applicant, flexShrink: 0, pt: 0.3 }}>
                {applicant}
              </Typography>

              {/* Actions */}
              <Box sx={{ width: COL.actions, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title="ดูรายละเอียด">
                  <IconButton size="small" onClick={() => { setIframeLoaded(false); setDetailId(item.expenses_id); }}>
                    <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Detail Modal */}
      <Modal open={!!detailId} onClose={() => setDetailId(null)}>
        <Fade in={!!detailId}>
          <Box sx={modalStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">รายละเอียดค่าใช้จ่าย</Typography>
              <IconButton size="small" onClick={() => setDetailId(null)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            {!iframeLoaded && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {detailId && (
              <iframe
                src={`/finance/expenses/detail/${detailId}?embed=1`}
                title="expense-detail"
                onLoad={() => setIframeLoaded(true)}
                style={{ display: iframeLoaded ? 'block' : 'none', width: '100%', flex: 1, border: 'none' }}
              />
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
